<template>
    <section
        class="suggestions"
        v-if="hasSuggestions">
        <div
            class="suggestions__column"
            v-if="suggestions.events.length > 0">
            <section
                class="suggestions__section">
                <h3 class="suggestions__group-heading"><span class="icon icon-clock"></span> Upcoming Events</h3>
                <ul class="suggestions__list">
                    <li
                        class="suggestions__item"
                        v-for="event in suggestions.events"
                        :key="event.id">
                        <router-link
                            class="suggestions__link"
                            tabindex="-1"
                            :class="{ 'suggestions__link--highlighted': highlightedSuggestion !== null && highlightedSuggestion.data === event }"
                            :to="makeEntityRoute('events', event)"
                            @click.native="onSuggestionClick({ data: event, type: 'events' })">
                            <slot name="event" :event="event">
                                <h4 class="suggestions__name">{{ event.name }}</h4>
                                <template v-if="event.is_date_tbd">Date TBD</template>
                                <template v-else-if="event.is_time_tbd">{{ event.local_date | date('ddd, MMM D') }}, Time TBD</template>
                                <template v-else>{{ event.local_date | date('ddd, MMM D, h:mma') }}</template>
                                &ndash; {{ event.venue }}<template v-if="event.city">, {{ event.city }}</template><template v-if="event.region">, {{ event.region }}</template>
                            </slot>
                        </router-link>
                    </li>
                </ul>
            </section>
        </div>
        <div
            class="suggestions__column"
            v-if="suggestions.performers.length > 0 || suggestions.venues.length > 0 || suggestions.cities.length > 0">
            <section
                class="suggestions__section"
                v-if="suggestions.performers.length > 0">
                <h3 class="suggestions__group-heading"><span class="icon icon-shirt"></span> Performers</h3>
                <ul class="suggestions__list">
                    <li
                        class="suggestions__item"
                        v-for="performer in suggestions.performers"
                        :key="performer.id">
                        <router-link
                            class="suggestions__link"
                            tabindex="-1"
                            :class="{ 'suggestions__link--highlighted': highlightedSuggestion !== null && highlightedSuggestion.data === performer }"
                            :to="makeEntityRoute('performers', performer)"
                            @click.native="onSuggestionClick({ data: performer, type: 'performers' })">
                            <slot name="performer" :performer="performer">
                                <h4 class="suggestions__name">{{ performer.name }}</h4>
                                <span v-if="isNumber(performer.count)">{{ `${performer.count} ${performer.count === 1 ? 'event' : 'events'}` }}</span>
                            </slot>
                        </router-link>
                    </li>
                </ul>
            </section>
            <section
                class="suggestions__section"
                v-if="suggestions.venues.length > 0">
                <h3 class="suggestions__group-heading"><span class="icon icon-venue"></span> Venues</h3>
                <ul class="suggestions__list">
                    <li
                        class="suggestions__item"
                        v-for="venue in suggestions.venues"
                        :key="venue.id">
                        <router-link
                            class="suggestions__link"
                            tabindex="-1"
                            :class="{ 'suggestions__link--highlighted': highlightedSuggestion !== null && highlightedSuggestion.data === venue }"
                            :to="makeEntityRoute('venues', venue)"
                            @click.native="onSuggestionClick({ data: venue, type: 'venues' })">
                            <slot name="venue" :venue="venue">
                                <h4 class="suggestions__name">{{ venue.name }}</h4>
                                <span v-if="isNumber(venue.count)">{{ `${venue.count} ${venue.count === 1 ? 'event' : 'events'}` }}</span>
                            </slot>
                        </router-link>
                    </li>
                </ul>
            </section>
            <section
                class="suggestions__section"
                v-if="suggestions.cities.length > 0">
                <h3 class="suggestions__group-heading"><span class="icon icon-location-marker"></span> Cities</h3>
                <ul class="suggestions__list">
                    <li
                        class="suggestions__item"
                        v-for="city in suggestions.cities"
                        :key="city.id">
                        <router-link
                            class="suggestions__link"
                            tabindex="-1"
                            :class="{ 'suggestions__link--highlighted': highlightedSuggestion !== null && highlightedSuggestion.data === city }"
                            :to="makeEntityRoute('cities', city)"
                            @click.native="onSuggestionClick({ data: city, type: 'cities' })">
                            <slot name="city" :city="city">
                                <h4 class="suggestions__name">{{ city.name }}</h4>
                                {{ `${city.count} ${city.count === 1 ? 'event' : 'events'}` }}
                            </slot>
                        </router-link>
                    </li>
                </ul>
            </section>
        </div>
    </section>
</template>

<script>
export default {
    computed: {
        hasSuggestions () {
            return this.suggestions !== null &&
                (this.suggestions.cities.length > 0 ||
                    this.suggestions.events.length > 0 ||
                    this.suggestions.performers.length > 0 ||
                    this.suggestions.venues.length > 0)
        }
    },
    methods: {
        isNumber (count) {
            // Partnership API will not return the event count by performer or venue,
            // so we hide the event count text if "count" is not a number
            return typeof count === 'number'
        }
    },
    props: {
        highlightedSuggestion: {
            default: null,
            required: false,
            type: Object
        },
        makeEntityRoute: {
            default (entityType, entity) {
                return ''
            },
            required: false,
            type: Function
        },
        onSuggestionClick: {
            default () {},
            required: false,
            type: Function
        },
        suggestions: {
            default: null,
            required: false,
            type: Object
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.suggestions {
    border-bottom: 1px solid rgba(255, 255, 255, .3);
    color: #fff;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: 0;
    width: 100%;

    & .suggestions__column {
        flex: 0 0 100%;
        padding: 0;
    }

    & .suggestions__group-heading {
        background: linear-gradient(0deg,rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.1));
        font-size: 14px;
        font-weight: normal;
        margin: 0;
        padding: 5px 16px;
        padding-left: 4px;

        & .icon {
            color: #fff;
            margin-right: 3px;
            right: 0px;
            top: 2px;
        }
    }

    & .suggestions__item {
        border-top: 1px solid rgba(255, 255, 255, 0.3);

        &:first-child {
            border-top: none;
        }
    }

    & .suggestions__link {
        color: #f5f5f5;
        display: block;
        font-weight: var(--fw-light);
        padding: 10px 20px 10px 26px;
        text-decoration: none;

        &.suggestions__link--highlighted,
        &:focus,
        &:hover {
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
            border-left: 6px solid #fff;
            padding-left: 20px;
        }
    }

    & .suggestions__list {
        border-top: 1px solid rgba(255, 255, 255, 0.3);
        list-style: none;
        margin: 0;
        padding: 0;
    }

    & .suggestions__name {
        font-size: var(--fs-larger);
        font-weight: normal;
        line-height: 1.3em;
        margin: 0 0 5px;
    }

    & .suggestions__section {
        border-top: 1px solid #fff;
        margin: 0;
    }

    @media (--mq-medium-min) {
        border-bottom: none;
        flex-direction: row;
        padding: 0 calc(var(--site-padding) / 2);

        & .suggestions__column {
            flex: 0 1 50%;
            padding: calc(var(--site-padding) / 2);
        }

        & .suggestions__group-heading {
            background: transparent;
            margin-bottom: 10px;
            padding: 0 16px;
            padding-left: 0;

            & .icon {
                position: relative;
                right: 1px;
                top: 2px;
            }
        }

        & .suggestions__link {
            padding: 10px 16px;

            &.suggestions__link--highlighted,
            &:focus,
            &:hover {
                padding-left: 10px;
            }
        }

        &  .suggestions__list {
            border-top: 2px solid #fff;
        }

        & .suggestions__section {
            border-top: none;
            margin-top: 10px;
        }
    }

    @media (--mq-full-min) {
        width: var(--site-container-width);
    }
}
</style>
